/**
 * Pure helpers for the forwarding-address surface (§ B8).
 *
 * The judgement here is the STATUS WORDING, and it is load-bearing rather than
 * cosmetic. The backend's ledger statuses are engineering words — `unmatched`,
 * `unattributed`, `needs_confirmation` — and a traveller reading them on their
 * own settings page has to learn, from six words, whether TripMuscle did
 * something, wants something, or dropped something.
 *
 * Two rules the copy must never break:
 *
 *   1. NEVER imply we recorded a booking we did not. `unmatched` means the
 *      email is sitting in a ledger doing nothing; saying "we're working on it"
 *      would be a lie the traveller only discovers when the leg they think is
 *      paid gets re-shopped.
 *   2. NEVER blame the traveller for our refusal. An ambiguous match is us
 *      declining to guess between two of their legs, not them forwarding a bad
 *      email. The action we offer is "pick the leg", not "try again".
 */

/** The ledger statuses, as the API returns them. */
export type ForwardedStatus =
  | 'pending'
  | 'unattributed'
  | 'unparsed'
  | 'needs_confirmation'
  | 'unmatched'
  | 'recorded'
  | 'dismissed'
  | 'error';

export interface ForwardedMessage {
  id: string;
  status: ForwardedStatus;
  status_detail: string | null;
  subject: string | null;
  from_address: string | null;
  sent_at: string | null;
  trip_id: string | null;
  route_id: string | null;
}

export type StatusTone = 'good' | 'attention' | 'inert' | 'bad';

export interface StatusPresentation {
  label: string;
  tone: StatusTone;
  /** True when the traveller can do something about it right now. */
  actionable: boolean;
}

/**
 * How one ledger row should read.
 *
 * `inert` is deliberately distinct from `bad`: a dismissed message is not a
 * failure, and an unattributed one is a message we could not prove was ours to
 * act on — neither should shout at the traveller in red.
 */
export function presentStatus(status: ForwardedStatus): StatusPresentation {
  switch (status) {
    case 'recorded':
      return { label: 'Booking recorded', tone: 'good', actionable: false };
    case 'needs_confirmation':
      // We wrote what we safely could; something still wants a human. The
      // traveller must be able to tell this from 'recorded' at a glance.
      return { label: 'Needs your confirmation', tone: 'attention', actionable: true };
    case 'unmatched':
      return { label: 'No matching leg — pick one', tone: 'attention', actionable: true };
    case 'unparsed':
      return { label: "Couldn't read this one", tone: 'inert', actionable: false };
    case 'unattributed':
      return { label: "Couldn't tell whose this was", tone: 'inert', actionable: false };
    case 'pending':
      return { label: 'Just arrived', tone: 'inert', actionable: false };
    case 'dismissed':
      return { label: 'Dismissed', tone: 'inert', actionable: false };
    case 'error':
      return { label: "Couldn't record it", tone: 'bad', actionable: false };
    default:
      // An unknown status from a newer backend must not render as blank.
      return { label: 'Unknown', tone: 'inert', actionable: false };
  }
}

/** The rows that actually want the traveller's attention, newest first. */
export function actionableMessages(messages: ForwardedMessage[]): ForwardedMessage[] {
  return messages.filter((m) => presentStatus(m.status).actionable);
}

/**
 * A one-line summary of the whole feature's state, for the card header.
 *
 * Ordered by what the traveller most needs to know: something waiting on them
 * beats a count of past successes, and "not configured" beats both because
 * nothing else on the card means anything if the mailbox is not being polled.
 */
export function summarise(opts: {
  configured: boolean;
  pendingCount: number;
  recordedCount: number;
}): string {
  if (!opts.configured) return 'Email forwarding is not set up on this server yet.';
  if (opts.pendingCount > 0) {
    return opts.pendingCount === 1
      ? '1 forwarded email needs your confirmation.'
      : `${opts.pendingCount} forwarded emails need your confirmation.`;
  }
  if (opts.recordedCount > 0) {
    return opts.recordedCount === 1
      ? '1 booking recorded from a forwarded email.'
      : `${opts.recordedCount} bookings recorded from forwarded emails.`;
  }
  return 'Forward an airline confirmation and it will land on the right trip.';
}

/**
 * When the poller last ran, in words.
 *
 * Returns null when it has never run, because "never" is genuinely different
 * from "a while ago" — it is the symptom of a scheduler that is not running,
 * and flattening it into "just now" would hide that.
 */
export function describeLastRun(lastRun: string | null | undefined, now = new Date()): string | null {
  if (!lastRun) return null;
  const t = new Date(lastRun).getTime();
  if (Number.isNaN(t)) return null;
  const mins = Math.floor((now.getTime() - t) / 60000);
  if (mins < 0) return 'just now';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
